import { getClientLocale, getClientTimeZone, getTimeZoneOffsetMinutes } from "./clientEnv.ts";

/**
 * Timestamps that reach the client fall into three kinds, and telling them
 * apart is the whole point of this module:
 *
 *  1. **Instants with a zone** — anything the backend types as
 *     `DateTimeOffset` (SimplePages kiosks, RMAC, PayaP) serializes with an
 *     offset, so plain `new Date(s)` is already correct. Nothing here needed.
 *
 *  2. **Zoneless wall clocks** — anything typed `DateTime`, or hand-formatted
 *     `"yyyy-MM-dd HH:mm:ss"`, serializes with no zone designator at all.
 *     `new Date("2026-08-13T07:10:32")` reads those as *browser*-local, which
 *     is only right by accident. Use `parseUtcTimestamp` when the stored value
 *     is UTC, `parseServerTimestamp` when it is SQL-server wall clock.
 *
 *  3. **Wall clocks belonging to somewhere else** — values a query already
 *     shifted into a kiosk's local time (`LastActivityLocal`, `EmptyAt`,
 *     `dKioskDate`). These are not instants; there is no zone to convert from.
 *     Render them verbatim with `formatWallClock` — passing them through
 *     `toLocaleString({ timeZone })` shifts them a second time.
 */

/**
 * The SQL server's UTC offset in minutes, published by AuthProvider from
 * InitCache's `ServerTimezoneOffset`.
 *
 * This has to come from the server: production runs UTC while dev boxes run
 * CET/CEST, so any constant compiled into the client is wrong in one of them.
 *
 * Defaults to 0 (server clock == UTC) when the backend does not supply a value,
 * which is production's case. On a CET/CEST server the backend MUST populate
 * `ServerTimezoneOffset` or zoneless server timestamps land an hour or two off.
 */
let serverUtcOffsetAccessor: () => number = () => 0;

export function setServerUtcOffsetAccessor(fn: () => number): void {
  serverUtcOffsetAccessor = fn;
}

/**
 * The server's IANA zone id, if the backend can tell us. Strictly better than the
 * scalar offset: an offset is a snapshot taken at sign-in, so a value from the
 * other half of the DST year converts an hour wrong (a January timestamp read in
 * August gets CEST's +02:00 instead of CET's +01:00). With a zone id we can ask
 * for the offset *at the value's own instant* instead.
 *
 * Unset today — `InitCache.ServerTimezoneOffset` had somewhere to live but there
 * is no zone-id field on the shared `WebCache` DTO. Until one exists every
 * conversion below falls back to the snapshot; see getServerOffsetMinutesAt.
 */
let serverTimeZoneAccessor: () => string | null = () => null;

export function setServerTimeZoneAccessor(fn: () => string | null): void {
  serverTimeZoneAccessor = fn;
}

/** The snapshot offset. Prefer getServerOffsetMinutesAt when you have an instant. */
export function getServerUtcOffsetMinutes(): number {
  const v = serverUtcOffsetAccessor();
  return Number.isFinite(v) ? v : 0;
}

/**
 * Server offset in minutes at a specific instant — DST-correct when a zone id is
 * available, otherwise the sign-in snapshot.
 */
export function getServerOffsetMinutesAt(at: Date): number {
  const zone = serverTimeZoneAccessor();
  if (zone) {
    try {
      return getTimeZoneOffsetMinutes(zone, at);
    } catch {
      // Unknown zone id — fall through to the snapshot.
    }
  }
  return getServerUtcOffsetMinutes();
}

/** True when the snapshot offset is all we have, so cross-DST values may be an
 *  hour out. Surfaced by the Date Gallery rather than hidden. */
export function isServerZoneKnown(): boolean {
  return serverTimeZoneAccessor() != null;
}

/** Zoneless datetime: "2026-08-13T07:10:32.51" or "2026-08-13 07:10:32". */
const ZONELESS_DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** Date with no time part: "2026-08-13". */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function normalize(raw: string): string {
  return raw.replace(" ", "T");
}

/**
 * Parses a timestamp whose stored value is UTC. Zoneless input gets the `Z` it
 * was missing; input that already carries a zone is left alone.
 */
export function parseUtcTimestamp(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const s = normalize(String(raw));
  const d = new Date(ZONELESS_DATETIME_RE.test(s) ? s + "Z" : s);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Parses a timestamp whose stored value is SQL-server wall clock (the
 * `GETDATE()` convention most tables follow) into the instant it names.
 *
 * Reads the wall clock as if UTC, then subtracts the server's offset *at that
 * point in time*. On a UTC server the subtraction is zero and this is a plain
 * UTC parse.
 *
 * Date-only input counts as zoneless too — it means midnight on the server, not
 * midnight UTC. `new Date("2026-08-13")` would have said the latter.
 */
export function parseServerTimestamp(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const s = normalize(String(raw));
  const zoneless = ZONELESS_DATETIME_RE.test(s) || DATE_ONLY_RE.test(s);
  if (!zoneless) {
    const asIs = new Date(s);
    return isNaN(asIs.getTime()) ? null : asIs;
  }
  const asUtc = new Date(DATE_ONLY_RE.test(s) ? s + "T00:00:00Z" : s + "Z");
  if (isNaN(asUtc.getTime())) return null;
  return new Date(asUtc.getTime() - getServerOffsetMinutesAt(asUtc) * 60000);
}

/* ── Calendar dates ──────────────────────────────────────────────────────
 *
 * A `<input type="date">` value and a SQL `[date]` column are both calendar
 * dates: no time, no zone. Routing one through an instant loses that. The
 * pattern to avoid is `new Date(serverValue).toISOString().slice(0, 10)` —
 * `new Date` reads a zoneless value as browser-local, `toISOString` converts
 * back to UTC, and east of Greenwich the round trip lands on the previous day.
 * Saving that value then persists the slip, so the date walks backwards one day
 * per save. Use these helpers instead; none of them build an instant.
 */

/** "YYYY-MM-DD" for a Date, read off its *local* calendar parts. */
export function toDateInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today's date in the viewer's zone — not UTC's, which can be a day off. */
export function todayDateInput(): string {
  return toDateInput(new Date());
}

/** "YYYY-MM-DDTHH:MM" from local parts, for `<input type="datetime-local">`. */
export function toDateTimeInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${toDateInput(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Calendar date of a server value, for an `<input type="date">`. Server dates
 * arrive as "YYYY-MM-DD" or "YYYY-MM-DDTHH:mm:ss", so the date part is taken
 * as text — no parsing, nothing to shift.
 */
export function serverDateToInput(raw: string | null | undefined): string {
  if (!raw) return "";
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : toDateInput(d);
}

/**
 * Inverse of `serverDateToInput`: a zoneless timestamp on the same calendar day,
 * which a `DateTime` property deserializes to that day's midnight.
 *
 * `hour` exists for endpoints that treat the value as an instant and shift it on
 * arrival (`WebSavePurchaseOrder` calls `.ToLocalTime`). Midday survives a shift
 * either way, so the calendar day holds whatever zone the server runs in.
 */
export function dateInputToServer(val: string | null | undefined, hour = 0): string | null {
  if (!val) return null;
  return `${val}T${String(hour).padStart(2, "0")}:00:00`;
}

/** Formats a true instant in the viewer's zone and locale (override-aware). */
export function formatInstant(
  d: Date | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!d || isNaN(d.getTime())) return "";
  return d.toLocaleString(getClientLocale(), { timeZone: getClientTimeZone(), ...options });
}

/**
 * Formats a zoneless wall clock verbatim — viewer's locale for ordering and
 * separators, but no zone projection, so the digits survive untouched. Parsing
 * is forced local (date-only strings would otherwise land on UTC midnight and
 * slip a day west of Greenwich).
 */
export function formatWallClock(
  raw: string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!raw) return "";
  let s = normalize(String(raw));
  if (DATE_ONLY_RE.test(s)) s += "T00:00:00";
  // Strip a zone designator if one is present: the caller has told us this
  // value is a wall clock, so honouring an offset would re-introduce a shift.
  s = s.replace(/(?:Z|[+-]\d{2}:?\d{2})$/i, "");
  const d = new Date(s);
  if (isNaN(d.getTime())) return String(raw);
  return options ? d.toLocaleString(getClientLocale(), options) : d.toLocaleString(getClientLocale());
}
